<template>
  <div class="shop-the-look">
    <template
      v-if="isMobile && hasMobileProducts && data?.mobileCollageTemplate"
    >
      <h4
        class="shop-the-look__heading text-heading-s"
        v-text="headingText"
      />

      <div>
        <shop-the-look-collage
          :products="mobileProducts"
          :template="data?.mobileCollageTemplate"
          @product-click="handleProductClick"
        />
      </div>
    </template>

    <template v-else-if="isMobile">
      <div
        class="shop-the-look__heading"
        v-html="'&nbsp;'"
      />
      <div class="shop-the-look__collage-loading" />
    </template>

    <template v-if="!isMobile && outfitProducts?.length">
      <h4
        ref="shopTheLookHeading"
        class="shop-the-look__heading text-body-2xs-regular"
        v-text="headingText"
      />

      <div class="shop-the-look__products">
        <a
          v-for="(product, index) of outfitProducts"
          :key="product.handle"
          class="shop-the-look__product shop-the-look__thumbnail"
          :class="{ [$class('active')]: fadeIn }"
          :aria-label="$string('shop_the_look.view_product_accessibility_text', {
            replace: { 'product': product.title },
          })"
          :href="product.url"
          :style="{ transitionDelay: `${index * 0.05}s` }"
          data-button="true"
          @click.prevent="handleProductClick(product)"
          @mouseenter="setHeadingText(product.title)"
          @mouseleave="setHeadingText(headingText)"
          @focusin="setHeadingText(product.title)"
          @focusout="setHeadingText(headingText)"
        >
          <span
            class="visually-hidden"
            v-text="product.title"
          />

          <responsive-image
            :image="getProductImage(product)"
            ratio="3:4"
            sizes="(min-width: 1024px) 25vw, 50vw"
          />
        </a>
      </div>
    </template>
  </div>
</template>

<script>

/**
 * Vue: Shop the look (shop-the-look)
 * -----------------------------------------------------------------------------
 * Shop the Look section.
 *
 * @param {String} source - Optional source to include when adding to cart
 * @param {String} outfit - The outfit metaobject key
 * @param {Object} mainProduct - The main product for which outfit is added
 *
 */
import { mapActions, mapState } from 'vuex'

import ResponsiveImage from '~global/images/responsive-image'

import ShopTheLookCollage from '~async/shop-the-look/shop-the-look-collage'

import outfitQuery from '~graphql/outfit.gql'
import { formatOutfit } from '~/format/format-outfit'

export default {
  name: 'ShopTheLook',

  components: {
    ResponsiveImage,
    ShopTheLookCollage,
  },

  props: {
    source: {
      type: [String, null],
      default: null,
    },
    outfit: {
      type: String,
      required: true,
    },
    mainProduct: {
      type: [Boolean, Object],
      default: false,
      required: true,
    },
  },

  data() {
    return {
      data: null,
      fadeIn: false,
      state: 'loading',
      showBackInStock: false,
    }
  },

  computed: {

    /**
     * Map Vuex state.
     */
    ...mapState({
      isMobile: (state) => state.index.screen.mobile,
    }),

    /**
     * Compute outfit products or loading state.
     * @returns {Array<Object>}
     */
    outfitProducts() {
      if (this.state === 'loading') {
        return [false, false, false].map((_, index) => {
          return {
            handle: `loading-${index}`,
            loading: true,
            title: this.$string('accessibility.loading'),
          }
        })
      }

      const products = [
        this.data?.product1 || null,
        this.data?.product2 || null,
        this.data?.product3 || null,
        this.data?.product4 || null,
        this.data?.product5 || null,
      ]

      return this.isMobile
        ? products
        : products.filter((prod) => prod && prod.id !== this.mainProduct.id)
    },

    /**
     * If there are mobile outfit products
     * @returns {Boolean}
     */
    hasMobileProducts() {
      return Boolean(this.data?.product1) ||
        Boolean(this.data?.product2) ||
        Boolean(this.data?.product3) ||
        Boolean(this.data?.product4) ||
        Boolean(this.data?.product5)
    },

    /**
     * The mobile products
     * @returns {Array}
     */
    mobileProducts() {
      return [
        this.data?.product1 || null,
        this.data?.product2 || null,
        this.data?.product3 || null,
        this.data?.product4 || null,
        this.data?.product5 || null,
      ]
    },

    /**
     * Compute the section's heading.
     * @returns {String} Heading
     */
    headingText() {
      if (!this.data?.title) {
        return this.$string('shop_the_look.heading_no_title')
      }

      return this.$string(
        'shop_the_look.heading',
        {
          replace: { outfit: this.data?.title || '' },
        },
      )
    },
  },

  watch: {

    /**
     * Load outfit data when key changes
     */
    outfit: {
      handler() {
        this.loadOutfit()
      },
      immediate: true,
    },

    /**
     * Watch state to update fade in.
     */
    async state() {
      if (this.state === 'loading') {
        this.fadeIn = false
        return
      }

      await this.$nextTick()
      this.fadeIn = true
    },
  },

  methods: {

    /**
     * Overlay actions
     */
    ...mapActions({
      openOverlay: 'overlays/open',
      setProductFormDrawerProduct: 'productFormDrawer/setProductFormDrawerProduct',
      setConfig: 'productFormDrawer/setConfig',
    }),

    /**
     * Get product image.
     * @param {Object} product - STL product.
     * @returns {Boolean|String}
     */
    getProductImage(product) {
      if (this.state === 'loading') {
        return {}
      }

      return product.selectedOrFirstAvailableVariant?.image ||
        product.variants[0]?.image ||
        product.media[0]?.image
    },

    /**
     * Open the quick add on product click
     * @param {Object} Product
     */
    handleProductClick(product) {
      if (product.loading) {
        return
      }

      const config = {
        strings: {
          return: this.$string('shop_the_look.return_to_look'),
          back: this.$string('shop_the_look.back_to_look'),
        },
        bisNamespace: 'shop-the-look',
      }

      if (this.source) {
        config.location = {
          key: 'pdpShopTheLook',
          metaobjectName: this.data?.metaobjectName || null,
        }
      }

      this.setProductFormDrawerProduct(product)
      this.setConfig(config)

      this.openOverlay({
        ignoreDismissed: true,
        namespace: 'product-form-drawer',
      })
    },

    /**
     * Loads an outfit's metaobject data
     * @returns {String} Heading
     */
    async loadOutfit() {
      if (!this.outfit || this.outfit === '') {
        this.data = null
        return
      }

      this.state = 'loading'

      if (this.$variable('settings.disableComponentReadyState')) {
        return
      }

      const data = await cnvs.Query({
        query: outfitQuery,
        variables: {
          country: this.$variable('store.country'),
          handle: {
            handle: this.outfit,
            type: 'outfit',
          },
          language: this.$variable('store.language'),
        },
      })

      this.data = formatOutfit(data)
      this.state = 'ready'
    },

    /**
     * Handles back in stock modal display
     * @param {Boolean} isActive - back in stock display state
     */
    handleBackInStockModal(isActive) {
      this.showBackInStock = isActive
    },

    /**
     * Sets the heading text
     * @param {String} text - heading text
     */
    setHeadingText(text) {
      this.$refs.shopTheLookHeading.innerHTML = text
    },
  },
}
</script>

<style lang="scss">
@import './shop-the-look';
</style>
